import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { appUrl } from '@astro/config'
import './BlogPostPage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import { useToast } from '../context/ToastContext'
import BlogCard from '../components/sections/blog/BlogCard'
import { authorCredential, authorName, decoratePost, formatDate, readTime, stripHtml } from '../components/sections/blog/blogShared'

function MiniPostItem({ post }) {
  return (
    <Link to={`/blog/${post.slug ?? post.id}`} className="sb-mini-item">
      {post.img ? (
        <img className="sb-mini-thumb" src={post.img} alt="" loading="lazy" />
      ) : (
        <span className="sb-mini-thumb sb-mini-thumb--empty" aria-hidden="true">ॐ</span>
      )}
      <div>
        <p className="sb-mini-title">{post.title}</p>
        <p className="sb-mini-meta">{post.date}</p>
      </div>
    </Link>
  )
}

export default function BlogPostPage() {
  const { slug } = useParams()
  const { showToast } = useToast()
  const [activeTocId, setActiveTocId] = useState(null)

  const { data: post, isPending, isError, refetch } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => api.blog.post(slug),
  })
  const { data: relatedRaw } = useQuery({
    queryKey: ['blog-related', slug],
    queryFn: () => api.blog.related(slug),
  })
  const { data: categories = [] } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.blog.categories(),
  })
  /* The sidebar shelf: the four newest articles, ordered by the API (four, so
     three are left after this one is filtered out). This used to pull a
     hundred posts and sort them in the browser. */
  const { data: newestData } = useQuery({
    queryKey: ['blog-posts', 'newest'],
    queryFn: () => api.blog.posts({ page: 1, per_page: 4, sort: 'newest' }),
  })

  const recentPosts = useMemo(
    () => (newestData?.data ?? []).map(decoratePost).filter((p) => p.slug !== slug).slice(0, 3),
    [newestData, slug],
  )

  const related = useMemo(() => {
    const fromApi = (relatedRaw ?? []).map(decoratePost).filter((p) => p.slug !== slug)
    if (fromApi.length) return fromApi.slice(0, 3)
    return recentPosts
  }, [relatedRaw, recentPosts, slug])

  // Inject anchor ids into the CMS HTML's h2 headings and derive the table of contents.
  const { html, toc } = useMemo(() => {
    if (!post?.content) return { html: '', toc: [] }
    const doc = new DOMParser().parseFromString(post.content, 'text/html')
    const headings = Array.from(doc.querySelectorAll('h2'))
    const entries = headings.map((h, i) => {
      const id = h.id || `section-${i + 1}`
      h.id = id
      return { id, label: h.textContent.trim() }
    })
    return { html: doc.body.innerHTML, toc: entries }
  }, [post])

  // Highlight the TOC entry whose section is currently in the viewport.
  useEffect(() => {
    if (!toc.length) return undefined
    const targets = toc.map((t) => document.getElementById(t.id)).filter(Boolean)
    if (!targets.length) return undefined
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveTocId(entry.target.id)
        })
      },
      { rootMargin: '-40% 0px -50% 0px' },
    )
    targets.forEach((t) => observer.observe(t))
    return () => observer.disconnect()
  }, [toc, html])

  const scrollToHeading = (id) => {
    const el = document.getElementById(id)
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 130, behavior: 'smooth' })
  }

  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
  const shareText = post ? post.title : 'Vedic Wisdom Blog'
  const shareLinks = [
    {
      label: 'Share on Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
        </svg>
      ),
    },
    {
      label: 'Share on Twitter / X',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`,
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
        </svg>
      ),
    },
    {
      label: 'Share on WhatsApp',
      href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${pageUrl}`)}`,
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M17.6 6.3A7.9 7.9 0 0 0 12.04 4a8 8 0 0 0-6.9 12l-1.14 4.15 4.25-1.11a8 8 0 0 0 3.79.96h0a8 8 0 0 0 5.66-13.7zM12.04 18.5a6.6 6.6 0 0 1-3.37-.92l-.24-.14-2.5.66.67-2.45-.16-.25a6.6 6.6 0 1 1 12.26-3.5 6.6 6.6 0 0 1-6.66 6.6z" />
        </svg>
      ),
    },
  ]

  const copyLink = () => {
    navigator.clipboard?.writeText(pageUrl)
    showToast('Link copied to clipboard')
  }

  const plain = post ? stripHtml(post.content) : ''
  const author = post ? authorName(post.author) : 'Astrologer Raju'
  // Astrologers publish here now, so the byline is theirs: their credential,
  // their photo, and a link to their profile where there is one. The page used
  // to print one hardcoded title and bio over every article.
  const credential = post ? authorCredential(post.author) : null
  const authorPhoto = post?.author?.photo_url ?? null
  const authorProfileUrl = post?.author?.astrologer_slug
    ? appUrl('jyotish', `/astrologers/${post.author.astrologer_slug}`)
    : null
  const badge = post?.category?.name ?? 'Insights'

  return (
    <PageLayout className="blogpost-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO ── */}
      <header className="relative pt-30 sm:pt-32.5 lg:pt-37.5 pb-40 lg:pb-48 overflow-hidden bg-[#5A0808] text-white text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/40 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.06] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mb-6 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <Link to="/blog" className="hover:text-goldPrimary transition-colors">Blog</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">{badge}</span>
          </nav>
          {isPending ? (
            <div className="animate-pulse space-y-5 max-w-xl mx-auto" aria-hidden="true">
              <div className="h-5 w-24 rounded-full bg-white/10 mx-auto" />
              <div className="h-9 w-full rounded bg-white/10" />
              <div className="h-9 w-3/4 rounded bg-white/10 mx-auto" />
              <div className="h-3 w-1/2 rounded bg-white/10 mx-auto" />
            </div>
          ) : (
            <>
              <span className="bp-badge">{badge}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-[46px] font-royal text-[#FBF3E4] leading-tight mt-5">
                {post?.title ?? 'Article Not Found'}
              </h1>
              {post && (
                <div className="flex items-center justify-center flex-wrap gap-x-5 gap-y-2 mt-7 text-[11px] uppercase tracking-widest font-semibold text-white/55">
                  <span className="flex items-center gap-2">
                    {authorPhoto ? (
                      <img src={authorPhoto} alt="" loading="lazy" className="w-6 h-6 rounded-full object-cover border border-goldPrimary/30" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-goldGlow/40 border border-goldPrimary/30 flex items-center justify-center text-goldPrimary font-bold text-[10px] font-royal normal-case">
                        {(author[0] || 'R').toUpperCase()}
                      </span>
                    )}
                    {author}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/25" />
                  <span className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 2v4M16 2v4" />
                      <rect width="18" height="18" x="3" y="4" rx="2" />
                      <path d="M3 10h18" />
                    </svg>
                    {formatDate(post.published_at)}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/25" />
                  <span className="flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {readTime(plain)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </header>

      {/* ── FEATURED IMAGE ── */}
      {(isPending || post?.featured_image_url) && (
        <section className="relative z-20 px-6">
          <div className="max-w-5xl mx-auto -mt-28 lg:-mt-32">
            <div className="rounded-[28px] overflow-hidden" style={{ boxShadow: '0 34px 80px -30px rgba(90,8,8,0.4)' }}>
              {isPending ? (
                <div className="w-full aspect-video animate-pulse" style={{ background: 'rgba(180,83,9,.15)' }} />
              ) : (
                <img src={post.featured_image_url} alt={post.title} className="w-full aspect-video object-cover" loading="lazy" />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── ERROR STATE ── */}
      {isError && (
        <section className="relative py-24" style={{ background: '#FDFAF5' }}>
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-royal text-[17px] font-bold text-[#3B2A1A] mb-1">Couldn&apos;t Load This Article</p>
            <p className="text-sm mb-6" style={{ color: '#8B7355' }}>
              Something went wrong while fetching the article. Please try again.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
                style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
              >
                Retry
              </button>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark shadow-lg"
              >
                Back to Blog
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── ARTICLE + SIDEBAR ── */}
      {!isError && (
        <section className="relative py-16 lg:py-20" style={{ background: '#FDFAF5' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14">

              {/* MAIN ARTICLE */}
              <article className="lg:col-span-8 article-prose">
                {isPending ? (
                  <div className="animate-pulse space-y-4" aria-hidden="true">
                    {Array.from({ length: 10 }, (_, i) => (
                      <div key={i} className="h-3.5 rounded-full" style={{ background: 'rgba(180,83,9,.10)', width: `${100 - (i % 4) * 9}%` }} />
                    ))}
                  </div>
                ) : (
                  <>
                    <div dangerouslySetInnerHTML={{ __html: html }} />

                    {/* Tags (inline) */}
                    {post?.category && (
                      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-amber-900/10">
                        <Link to={`/blog?category=${post.category.slug}`} className="sb-tag">{post.category.name}</Link>
                      </div>
                    )}
                  </>
                )}
              </article>

              {/* SIDEBAR */}
              <aside className="lg:col-span-4 space-y-6">
                <div className="lg:sticky lg:top-32 space-y-6">

                  {/* Author card */}
                  <Reveal className="sb-card text-center">
                    {authorPhoto ? (
                      <img
                        src={authorPhoto}
                        alt={author}
                        loading="lazy"
                        className="w-16 h-16 rounded-full object-cover border border-goldPrimary/30 mx-auto"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-goldGlow/20 border border-goldPrimary/30 flex items-center justify-center text-amber-700 font-bold text-2xl font-royal mx-auto">
                        {(author[0] || 'R').toUpperCase()}
                      </div>
                    )}
                    <p className="font-royal text-base text-astroDark mt-3">{author}</p>
                    {/* Both lines are the author's own, from the API — nothing
                        here is written in advance for one person. */}
                    {credential && (
                      <p className="text-[12.5px] text-stone-500 font-light leading-relaxed mt-2">{credential}</p>
                    )}
                    {authorProfileUrl ? (
                      <a
                        href={authorProfileUrl}
                        className="shine-btn inline-flex items-center gap-2 mt-4 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[.16em] px-6 py-2.5 rounded-full shadow-lg transition-all duration-300 hover:scale-105"
                      >
                        Book a Session
                      </a>
                    ) : (
                      <Link
                        to="/#consultation"
                        className="shine-btn inline-flex items-center gap-2 mt-4 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[.16em] px-6 py-2.5 rounded-full shadow-lg transition-all duration-300 hover:scale-105"
                      >
                        Book a Session
                      </Link>
                    )}
                  </Reveal>

                  {/* Table of Contents */}
                  {toc.length > 0 && (
                    <Reveal className="sb-card">
                      <h4 className="sb-head">Table of Contents</h4>
                      <nav className="flex flex-col">
                        {toc.map((t, i) => (
                          <button
                            key={t.id}
                            type="button"
                            className={`sb-toc-link${activeTocId === t.id ? ' active' : ''}`}
                            onClick={() => scrollToHeading(t.id)}
                          >
                            <span className="sb-toc-num">{String(i + 1).padStart(2, '0')}</span> {t.label}
                          </button>
                        ))}
                      </nav>
                    </Reveal>
                  )}

                  {/* Share */}
                  <Reveal className="sb-card">
                    <h4 className="sb-head">Share This Article</h4>
                    <div className="flex items-center gap-3">
                      {shareLinks.map((s) => (
                        <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="sb-share-btn" aria-label={s.label}>
                          {s.icon}
                        </a>
                      ))}
                      <button type="button" className="sb-share-btn" aria-label="Copy link" onClick={copyLink}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      </button>
                    </div>
                  </Reveal>

                  {/* Categories */}
                  {categories.length > 0 && (
                    <Reveal className="sb-card">
                      <h4 className="sb-head">Categories</h4>
                      {categories.map((c) => (
                        <Link key={c.id} to={`/blog?category=${c.slug}`} className="sb-cat-link">
                          {c.name}
                          {typeof c.posts_count === 'number' && <span className="sb-cat-count">{c.posts_count}</span>}
                        </Link>
                      ))}
                    </Reveal>
                  )}

                  {/* Recent Posts */}
                  {recentPosts.length > 0 && (
                    <Reveal className="sb-card">
                      <h4 className="sb-head">Recent Posts</h4>
                      {recentPosts.map((p) => (
                        <MiniPostItem key={p.id} post={p} />
                      ))}
                    </Reveal>
                  )}

                </div>
              </aside>

            </div>
          </div>
        </section>
      )}

      {/* ── RELATED ARTICLES ── */}
      {!isError && related.length > 0 && (
        <section className="relative py-16 lg:py-20" style={{ background: 'linear-gradient(160deg,#FDFAF5 0%,#F7F0E4 45%,#FDFAF5 100%)' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <Reveal className="flex items-center gap-3 mb-10">
              <h2 className="text-2xl sm:text-3xl text-astroDark">Related Articles</h2>
              <span className="flex-1 h-px bg-amber-900/10" />
              <Link to="/blog" className="text-[11px] font-bold uppercase tracking-widest text-amber-700 hover:text-amber-900 transition-colors whitespace-nowrap">
                View All →
              </Link>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Reveal key={p.id} className="h-full">
                  <BlogCard post={p} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA — BOOK CONSULTATION ── */}
      <section className="relative overflow-hidden py-20 lg:py-24 bg-[#5A0808] text-white text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] bg-cosmicRed/40 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] aspect-square opacity-[0.05] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
          </svg>
        </div>
        <Reveal className="relative z-10 max-w-2xl mx-auto px-6">
          <span className="text-[20px] text-goldPrimary/70">ॐ</span>
          <h2 className="text-3xl sm:text-4xl text-[#FBF3E4] mt-3">Ready for Personalized Guidance?</h2>
          <p className="text-sm text-white/50 font-light mt-4 leading-relaxed max-w-md mx-auto">
            Get a detailed Kundli matching report or a full life reading directly from Maharaj Ji — rooted in 15+ years of authentic Vedic practice.
          </p>
          <Link
            to="/#consultation"
            className="shine-btn inline-flex items-center gap-2.5 bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[.22em] px-10 py-4 rounded-full shadow-xl transition-all duration-300 hover:scale-105 mt-8"
          >
            Book Consultation
            <span className="text-base leading-none">→</span>
          </Link>
        </Reveal>
      </section>

    </PageLayout>
  )
}
